import React, { useMemo, useState } from 'react';
import './SchoolInvites.css';

function SchoolInvites({ invites, onRefresh }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('teacher');
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (invite) => {
    setEditingId(invite.id);
    setEditEmail(invite.email);
    setEditRole((invite.role || 'teacher').toLowerCase());
  };
  const cancelEdit = () => { setEditingId(null); setSavingEdit(false); };

  // Correct an outstanding invite's email and/or role. If the email changed, the server
  // re-sends the code to the corrected address.
  const handleSaveEdit = async (invite) => {
    if (savingEdit) return;
    const email = editEmail.trim();
    if (!email || !email.includes('@')) { alert('Enter a valid email address.'); return; }
    setSavingEdit(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/invite/${invite.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
        body: JSON.stringify({ email, role: editRole })
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.resent ? `Invite updated. A new code was sent to ${data.email}.` : 'Invite updated.');
        setEditingId(null);
        if (onRefresh) await onRefresh();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to update the invite. Please try again.');
      }
    } catch (error) {
      console.error('Error updating invite:', error);
      alert('Error updating the invite. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  // Re-send the signup code for EVERY outstanding invite at this school (e.g. after a server
  // restart wiped the codes, or a batch of emails never arrived).
  const handleResendAll = async () => {
    const schoolId = invites[0]?.schoolId;
    if (!schoolId || resendingAll) return;
    if (!window.confirm(`Email a fresh code to all ${invites.length} pending invite(s) at this school?`)) return;
    setResendingAll(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/school/${schoolId}/resend-invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(`Re-sent codes to ${data.resent ?? invites.length} pending invite(s).`);
        if (onRefresh) await onRefresh();
      } else {
        alert('Failed to resend invites. Please try again.');
      }
    } catch (error) {
      console.error('Error resending all invites:', error);
      alert('Error resending invites. Please try again.');
    } finally {
      setResendingAll(false);
    }
  };

  // Re-send an invite: mints a FRESH signup code (new 21-day window) for the same
  // email/role/school and emails it. The old code keeps working until it expires or
  // the account is created — re-sending never creates a duplicate account.
  const handleResend = async (invite) => {
    if (!window.confirm(`Email a new invite code to ${invite.email}?`)) return;
    setResendingId(invite.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify({
          emails: [invite.email],
          role: invite.role,
          schoolId: invite.schoolId
        })
      });
      if (response.ok) {
        alert(`New invite code sent to ${invite.email}`);
        if (onRefresh) await onRefresh();
      } else {
        console.error('Failed to resend invite:', response.status);
        alert('Failed to resend invite. Please try again.');
      }
    } catch (error) {
      console.error('Error resending invite:', error);
      alert('Error resending invite. Please try again.');
    } finally {
      setResendingId(null);
    }
  };
  // Sort invites: administrators first, then by most recent
  const sortedInvites = useMemo(() => {
    return [...invites].sort((a, b) => {
      // Sort by role first (ADMIN comes before others)
      const roleOrder = { 'administrator': 0, 'teacher': 1, 'staff': 2 };
      const roleA = roleOrder[a.role.toLowerCase()] ?? 99;
      const roleB = roleOrder[b.role.toLowerCase()] ?? 99;

      if (roleA !== roleB) {
        return roleA - roleB;
      }

      // If same role, sort by most recent first
      return b.sentTime - a.sentTime;
    });
  }, [invites]);

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteClick = (invite) => {
    setInviteToDelete(invite);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inviteToDelete) return;

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/invite/${inviteToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      console.log('Delete invite response:', response);

      if (response.ok) {
        console.log('Invite deleted successfully');
        alert('Invite deleted successfully');
        
        // Close confirmation dialog
        setShowDeleteConfirm(false);
        setInviteToDelete(null);
        
        // Refresh the data
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        console.error('Failed to delete invite:', response.status);
        alert('Failed to delete invite. Please try again.');
        setShowDeleteConfirm(false);
        setInviteToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting invite:', error);
      alert('Error deleting invite. Please try again.');
      setShowDeleteConfirm(false);
      setInviteToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setInviteToDelete(null);
  };

  return (
    <div className="school-invites">
      <div className="invites-heading">
        <h3>Pending Invites ({invites.length})</h3>
        {invites.length > 0 && (
          <button className="resend-all-button" onClick={handleResendAll} disabled={resendingAll}>
            {resendingAll ? 'Sending…' : 'Resend all codes'}
          </button>
        )}
      </div>
      <div className="invites-list">
        {invites.length === 0 ? (
          <div className="no-invites">No pending invites</div>
        ) : (
          sortedInvites.map(invite => (
            <div key={invite.id} className="invite-card">
              {editingId === invite.id ? (
                <div className="invite-edit">
                  <input
                    className="invite-edit-email"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="email@school.org"
                  />
                  <select className="invite-edit-role" value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                    <option value="teacher">Teacher</option>
                    <option value="administrator">Administrator</option>
                  </select>
                  <div className="invite-edit-actions">
                    <button className="invite-edit-cancel" onClick={cancelEdit} disabled={savingEdit}>Cancel</button>
                    <button className="invite-edit-save" onClick={() => handleSaveEdit(invite)} disabled={savingEdit}>
                      {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <div className="invite-edit-hint">Changing the email re-sends the code to the new address.</div>
                </div>
              ) : (
                <>
                  <div className="invite-header">
                    <div className="invite-info">
                      <span className="invite-email">{invite.email}</span>
                      <span className={`invite-role role-${invite.role.toLowerCase()}`}>
                        {invite.role}
                      </span>
                    </div>
                    <div className="invite-actions">
                      <button
                        onClick={() => openEdit(invite)}
                        className="edit-invite-button"
                        title="Edit email / role"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleResend(invite)}
                        className="resend-invite-button"
                        title="Email a new invite code"
                        disabled={resendingId === invite.id}
                      >
                        {resendingId === invite.id ? 'Sending…' : 'Resend code'}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(invite)}
                        className="delete-invite-button"
                        title="Delete invite"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="invite-time">
                    Sent: {formatDate(invite.sentTime)}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {showDeleteConfirm && inviteToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="modal-close" onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this invite?</p>
              <p className="invite-details">
                <strong>{inviteToDelete.email}</strong>
                <br />
                Role: {inviteToDelete.role}
              </p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="modal-button delete-confirm" onClick={handleDeleteConfirm}>
                Delete Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolInvites;

